/** */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionService } from 'hooks/api/sales/subscriptions';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { Spacer } from 'app/member/layouts/generic';
import { Button } from 'app/shared/button';
import { DetailItem } from 'app/shared/info-section/item';
import { formatDateForDisplay } from 'app/shared/utils/date';
import { DAY_MS } from 'app/member/modules/billing/subscriptions/utils';
import { fmt } from 'utilities/format';
import styles from './detail.module.css';

const THREE_MONTHS_DAYS = 90;

const formatTier = (tier) => (tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : '—');

const formatBracket = (bracket) =>
  bracket ? `${fmt(bracket.min)} – ${fmt(bracket.max)} contacts` : '—';

export const OpportunityDetail = ({ row = {} }) => {
  const navigate = useNavigate();
  const { fetchSubscriptions } = useSubscriptionService();
  const { tenant = {}, opportunity_breakdown: breakdown } = row;
  const { metadata = {} } = tenant;

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    handleSubscriptionsRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id]);

  const handleSubscriptionsRequest = async () => {
    setLoading(true);
    const now = Date.now();
    const three_months_ago = now - THREE_MONTHS_DAYS * DAY_MS;
    const { subscriptions: tenant_subscriptions } = await fetchSubscriptions({
      query_string: `tenant_id=${tenant.id}&time_stamp=${three_months_ago}~${now}&sort_by=-time_stamp&population=100`
    });
    setSubscriptions(tenant_subscriptions);
    setLoading(false);
  };

  const renderSubscriptions = () => {
    if (loading) return <div className={styles.empty}>Loading subscriptions…</div>;
    if (!subscriptions.length) {
      return <div className={styles.empty}>No subscriptions in the last 3 months</div>;
    }

    return (
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Plan</th>
            <th>Amount</th>
            <th>Purchased</th>
            <th>Expires</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {subscriptions.map((subscription) => (
            <tr key={subscription.id}>
              <td>{subscription.title || subscription.plan_code || '—'}</td>
              <td className={styles.amount}>
                {subscription.price
                  ? `${fmt(subscription.price.amount)} ${subscription.price.currency?.code || ''}`
                  : '—'}
              </td>
              <td>{formatDateForDisplay(subscription.purchased_at) || '—'}</td>
              <td>{formatDateForDisplay(subscription.expires_at) || '—'}</td>
              <td>{subscription.status || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderOpportunity = () => {
    if (!breakdown) {
      return (
        <div className={styles.empty}>
          No opportunity — the tenant is on Enterprise, has never subscribed, or their contact count
          is outside the priced brackets.
        </div>
      );
    }

    if (breakdown.type === 'reactivation') {
      return (
        <GridRow num_of_columns={3}>
          <GridColumn>
            <DetailItem title="Opportunity Type">Reactivation</DetailItem>
          </GridColumn>
          <GridColumn>
            <DetailItem title="Previous Plan">
              {formatTier(breakdown.previous_tier) !== '—'
                ? formatTier(breakdown.previous_tier)
                : breakdown.previous_plan || '—'}
            </DetailItem>
          </GridColumn>
          <GridColumn>
            <DetailItem title="Lapsed On">
              {formatDateForDisplay(breakdown.last_expired_at) || '—'}
            </DetailItem>
          </GridColumn>
          <GridColumn>
            <DetailItem title="Contact Bracket">
              {formatBracket(breakdown.contact_bracket)}
            </DetailItem>
          </GridColumn>
          <GridColumn>
            <DetailItem title="Last Paid">{fmt(breakdown.last_paid, 'currency')}</DetailItem>
          </GridColumn>
          <GridColumn>
            <DetailItem title="Opportunity">
              <span className={styles.opportunity}>{fmt(breakdown.value, 'currency')}</span>
            </DetailItem>
          </GridColumn>
        </GridRow>
      );
    }

    return (
      <GridRow num_of_columns={3}>
        <GridColumn>
          <DetailItem title="Current Plan">{formatTier(breakdown.current_tier)}</DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Upgrade To">{formatTier(breakdown.upgrade_tier)}</DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Contact Bracket">
            {formatBracket(breakdown.contact_bracket)}
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Current Price">{fmt(breakdown.current_price, 'currency')}</DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Upgrade Price">{fmt(breakdown.upgrade_price, 'currency')}</DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Opportunity">
            <span className={styles.opportunity}>{fmt(breakdown.value, 'currency')}</span>
          </DetailItem>
        </GridColumn>
      </GridRow>
    );
  };

  return (
    <>
      <GridRow num_of_columns={3}>
        <GridColumn>
          <DetailItem title="Name">{tenant.name || '—'}</DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Email">{tenant.emails?.[0] || '—'}</DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Phone">{tenant.phone_number || '—'}</DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Contacts">{fmt(metadata.contacts_count)}</DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Monthly Spend">
            {fmt(metadata.average_monthly_spend, 'currency')}
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Last Active">
            {formatDateForDisplay(metadata.last_activity) || '—'}
          </DetailItem>
        </GridColumn>
      </GridRow>

      <hr className={styles.divider} />

      {renderOpportunity()}

      <hr className={styles.divider} />

      {renderSubscriptions()}

      <Spacer multiple={4} />

      <GridRow num_of_columns={4}>
        <GridColumn span={3} />
        <GridColumn>
          <Button
            text="View Tenant"
            type="secondary"
            onClick={() => navigate(`/tenants/${tenant.id}/dashboard`)}
          />
        </GridColumn>
      </GridRow>
    </>
  );
};
